/**
 * delivery-countdown.js
 *
 * Two countdowns:
 *
 *   .delivery-countdown   the dispatch/delivery line rendered by
 *                         snippets/delivery-message.liquid — Liquid picks the
 *                         dates, this counts down to the cutoff.
 *   <countdown-timer>     a generic days/hours/mins/secs countdown to a fixed
 *                         data-end-date, for promos and offer banners.
 *
 * Split out of product.js so pages that only need a countdown do not carry the
 * whole PDP bundle.
 */

/* The upstream <countdown-timer> defers its own start with initLazyScript.
   This theme has no such helper, so stand one in: run on the next frame once
   the element's children are parsed. */
if (!window.initLazyScript) {
  window.initLazyScript = function (el, callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      window.requestAnimationFrame(callback);
    }
  };
}

/* =============================================================================
   Delivery countdown — ticks the digits in snippets/delivery-message.liquid

   The dispatch and delivery dates are already worked out in Liquid; this only
   counts down to the cutoff time carried on the span.
   ============================================================================= */
function initDeliveryCountdowns(root) {
  (root || document).querySelectorAll('.delivery-countdown').forEach((element) => {
    if (element.dataset.countdownBound) return;
    element.dataset.countdownBound = 'true';

    const cutoffTime = element.getAttribute('data-cutoff-time');
    const sameDay = element.getAttribute('data-same-day') === 'true';
    let lastRendered = '';

    if (!cutoffTime) return;

    const [cutoffHour, cutoffMinute] = cutoffTime.split(':').map(Number);
    if (Number.isNaN(cutoffHour) || Number.isNaN(cutoffMinute)) return;

    function getTargetDate() {
      const now = new Date();
      const target = new Date(now);
      target.setHours(cutoffHour, cutoffMinute, 0, 0);

      // After-cutoff flow always targets the next day's cutoff.
      if (!sameDay || now.getTime() >= target.getTime()) {
        target.setDate(target.getDate() + 1);
      }

      return target;
    }

    function updateCountdown() {
      const timeLeftMs = getTargetDate().getTime() - Date.now();

      if (timeLeftMs <= 0) {
        element.textContent = '00:00:00';
        return;
      }

      const totalSeconds = Math.floor(timeLeftMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const nextValue =
        String(hours).padStart(2, '0') + ':' +
        String(minutes).padStart(2, '0') + ':' +
        String(seconds).padStart(2, '0');

      // Skip the DOM write when nothing changed — less jitter.
      if (nextValue !== lastRendered) {
        element.textContent = nextValue;
        lastRendered = nextValue;
      }
    }

    updateCountdown();

    // setInterval drifts, so over a long session a tick lands just either side
    // of a second boundary and a digit repeats or skips. Re-aim at the next
    // whole second after every update instead.
    (function tick() {
      setTimeout(() => {
        updateCountdown();
        tick();
      }, 1000 - (Date.now() % 1000));
    })();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initDeliveryCountdowns());
} else {
  initDeliveryCountdowns();
}

// Section Rendering API / theme editor re-renders bring new spans with them.
document.addEventListener('shopify:section:load', (e) => initDeliveryCountdowns(e.target));

/* =============================================================================
   CountdownTimer — countdown to a fixed date
   ============================================================================= */
if (!customElements.get('countdown-timer')) {
  class CountdownTimer extends HTMLElement {
    constructor() {
      super();
      this.endDate = new Date(this.dataset.endDate).getTime();
      if (!Number(this.endDate)) return;

      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      this.daysEl = this.querySelector('.js-days');
      this.hoursEl = this.querySelector('.js-hours');
      this.minsEl = this.querySelector('.js-mins');
      this.secsEl = this.querySelector('.js-secs');
      this.padNumbers = this.dataset.padNumbers === 'true';

      this.second = 1000;
      this.minute = 60 * this.second;
      this.hour = 60 * this.minute;
      this.day = 24 * this.hour;

      this.timer();
      this.interval = setInterval(this.timer.bind(this), this.second);
    }

    timer() {
      const timeDiff = this.endDate - new Date();

      if (timeDiff < 0) {
        clearInterval(this.interval);
        if (this.daysEl && this.hoursEl && this.minsEl && this.secsEl) {
          const z = this.padNumbers ? '00' : '0';
          this.daysEl.textContent = z;
          this.hoursEl.textContent = z;
          this.minsEl.textContent = z;
          this.secsEl.textContent = z;
        }
        return;
      }

      if (!this.daysEl || !this.hoursEl || !this.minsEl || !this.secsEl) {
        clearInterval(this.interval);
        return;
      }

      const days = Math.floor(timeDiff / this.day);
      const hours = Math.floor(timeDiff / this.hour);
      const mins = Math.floor(timeDiff / this.minute);
      const secs = Math.floor(timeDiff / this.second);

      const d = days;
      const h = hours - days * 24;
      const m = mins - hours * 60;
      const s = secs - mins * 60;

      const fmt = (n) => (this.padNumbers ? String(n).padStart(2, '0') : String(n));

      this.daysEl.textContent = fmt(d);
      this.hoursEl.textContent = fmt(h);
      this.minsEl.textContent = fmt(m);
      this.secsEl.textContent = fmt(s);
    }
  }

  customElements.define('countdown-timer', CountdownTimer);
}
